/**
 * IBAN Validation
 *
 * Validates International Bank Account Numbers.
 * Turkey: TR + 2 check digits + 5 bank code + 1 reserved + 16 account = 26 chars.
 * US does not use IBAN (uses routing + account number instead).
 */

const IBAN_LENGTHS: Record<string, number> = {
  TR: 26,
  DE: 22,
  GB: 22,
  FR: 27,
  NL: 18,
};

/**
 * Validate IBAN checksum (ISO 13616 MOD-97-10).
 */
function validateIBANChecksum(iban: string): boolean {
  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const numericString = rearranged
    .split('')
    .map((c) => {
      const code = c.charCodeAt(0);
      return code >= 65 && code <= 90 ? (code - 55).toString() : c;
    })
    .join('');

  // BigInt modulo for large numbers
  let remainder = 0n;
  for (let i = 0; i < numericString.length; i += 7) {
    const chunk = numericString.slice(i, i + 7);
    remainder = BigInt(`${remainder}${chunk}`) % 97n;
  }

  return remainder === 1n;
}

export interface IBANValidationResult {
  valid: boolean;
  countryCode?: string;
  bankCode?: string;
  error?: string;
}

/**
 * Validate an IBAN string.
 */
export function validateIBAN(iban: string): IBANValidationResult {
  const cleaned = iban.replace(/\s/g, '').toUpperCase();

  if (cleaned.length < 5) {
    return { valid: false, error: 'IBAN too short' };
  }

  const countryCode = cleaned.slice(0, 2);
  const expectedLength = IBAN_LENGTHS[countryCode];

  if (expectedLength && cleaned.length !== expectedLength) {
    return {
      valid: false,
      countryCode,
      error: `${countryCode} IBAN must be ${expectedLength} characters, got ${cleaned.length}`,
    };
  }

  if (!/^[A-Z]{2}\d{2}[A-Z0-9]+$/.test(cleaned)) {
    return { valid: false, error: 'Invalid IBAN format' };
  }

  if (!validateIBANChecksum(cleaned)) {
    return { valid: false, countryCode, error: 'Invalid IBAN checksum' };
  }

  const bankCode = countryCode === 'TR' ? cleaned.slice(4, 9) : cleaned.slice(4, 8);

  return { valid: true, countryCode, bankCode };
}

/**
 * Validate a Turkish IBAN specifically.
 * Format: TR + 2 check + 5 bank code + 1 reserved + 16 account number
 */
export function validateTurkishIBAN(iban: string): IBANValidationResult {
  const cleaned = iban.replace(/\s/g, '').toUpperCase();

  if (!cleaned.startsWith('TR')) {
    return { valid: false, error: 'Not a Turkish IBAN (must start with TR)' };
  }

  if (!/^TR\d{24}$/.test(cleaned)) {
    return { valid: false, error: 'Turkish IBAN must be TR followed by 24 digits' };
  }

  return validateIBAN(cleaned);
}
